import { useI18n } from '../i18n'
import { Select, Switch } from './ui'
import { StatusPill } from './v2/V2Primitives'

export const MONETIZATION_ROUTE_SETTING_DEFAULTS = Object.freeze({
  physical_item_sales_enabled: false,
  physical_item_currency: 'USD',
  physical_item_underwear_starting_price: 75,
  physical_item_clothing_starting_price: 90,
  physical_item_other_starting_price: 60,
  physical_item_worn_day_markup: 15,
  physical_item_personalization_markup: 15,
  support_route_enabled: false,
  support_afterglow_enabled: false,
  support_afterglow_min_score: 75,
  support_afterglow_cooldown_days: 14,
  support_chat_gate_enabled: false,
  support_chat_gate_message_limit: 12,
  support_chat_gate_min_tip_amount: 5,
  support_chat_gate_currency: 'USD',
  support_chat_gate_cooldown_days: 1,
  support_wishlist_enabled: false,
  support_wishlist_url: '',
  support_wishlist_profile_linked: false,
  support_campaign_enabled: false,
  support_campaign_reason: '',
  support_campaign_goal_amount: 0,
  support_campaign_currency: 'USD',
  support_campaign_cooldown_days: 14,
  adaptive_dominant_style_enabled: false,
  adaptive_dominant_style_min_score: 70,
  adaptive_receptive_style_enabled: false,
  adaptive_power_style_max_intensity: 2,
  adaptive_interaction_style_enabled: false,
  adaptive_interaction_style_min_confidence: 65,
})

export const MONETIZATION_ROUTE_SETTING_KEYS = Object.keys(MONETIZATION_ROUTE_SETTING_DEFAULTS)

export const MONETIZATION_ROUTE_BOOLEAN_SETTING_KEYS = new Set([
  'physical_item_sales_enabled',
  'support_route_enabled',
  'support_afterglow_enabled',
  'support_chat_gate_enabled',
  'support_wishlist_enabled',
  'support_wishlist_profile_linked',
  'support_campaign_enabled',
  'adaptive_dominant_style_enabled',
  'adaptive_receptive_style_enabled',
  'adaptive_interaction_style_enabled',
])

export const MONETIZATION_ROUTE_STRING_SETTING_KEYS = new Set([
  'physical_item_currency',
  'support_campaign_reason',
  'support_campaign_currency',
  'support_wishlist_url',
])

const ROUTE_GROUPS = [
  {
    key: 'physical',
    badge: 'manualReview',
    keys: [
      'physical_item_sales_enabled',
      'physical_item_currency',
      'physical_item_underwear_starting_price',
      'physical_item_clothing_starting_price',
      'physical_item_other_starting_price',
      'physical_item_worn_day_markup',
      'physical_item_personalization_markup',
    ],
  },
  {
    key: 'support',
    badge: 'modelApproved',
    keys: [
      'support_route_enabled',
      'support_afterglow_enabled',
      'support_afterglow_min_score',
      'support_afterglow_cooldown_days',
      'support_chat_gate_enabled',
      'support_chat_gate_message_limit',
      'support_chat_gate_min_tip_amount',
      'support_chat_gate_currency',
      'support_chat_gate_cooldown_days',
      'support_wishlist_enabled',
      'support_wishlist_url',
      'support_wishlist_profile_linked',
      'support_campaign_enabled',
      'support_campaign_reason',
      'support_campaign_goal_amount',
      'support_campaign_currency',
      'support_campaign_cooldown_days',
    ],
  },
  {
    key: 'interaction',
    badge: 'explicitSignal',
    keys: [
      'adaptive_interaction_style_enabled',
      'adaptive_interaction_style_min_confidence',
      'adaptive_dominant_style_enabled',
      'adaptive_dominant_style_min_score',
      'adaptive_receptive_style_enabled',
      'adaptive_power_style_max_intensity',
    ],
  },
]

const FIELD_META = {
  physical_item_sales_enabled: { type: 'boolean' },
  physical_item_currency: { type: 'select', options: ['USD', 'EUR', 'GBP'] },
  physical_item_underwear_starting_price: { type: 'number', min: 0, max: 100000, step: 0.01 },
  physical_item_clothing_starting_price: { type: 'number', min: 0, max: 100000, step: 0.01 },
  physical_item_other_starting_price: { type: 'number', min: 0, max: 100000, step: 0.01 },
  physical_item_worn_day_markup: { type: 'number', min: 0, max: 100000, step: 0.01 },
  physical_item_personalization_markup: { type: 'number', min: 0, max: 100000, step: 0.01 },
  support_route_enabled: { type: 'boolean' },
  support_afterglow_enabled: { type: 'boolean' },
  support_afterglow_min_score: { type: 'number', min: 50, max: 100, step: 1 },
  support_afterglow_cooldown_days: { type: 'number', min: 1, max: 365, step: 1 },
  support_chat_gate_enabled: { type: 'boolean' },
  support_chat_gate_message_limit: { type: 'number', min: 4, max: 200, step: 1 },
  support_chat_gate_min_tip_amount: { type: 'number', min: 0.01, max: 100000, step: 0.01 },
  support_chat_gate_currency: { type: 'select', options: ['USD', 'EUR', 'GBP'] },
  support_chat_gate_cooldown_days: { type: 'number', min: 1, max: 365, step: 1 },
  support_wishlist_enabled: { type: 'boolean' },
  support_wishlist_url: { type: 'text' },
  support_wishlist_profile_linked: { type: 'boolean' },
  support_campaign_enabled: { type: 'boolean' },
  support_campaign_reason: { type: 'textarea', rows: 5 },
  support_campaign_goal_amount: { type: 'number', min: 0, max: 100000, step: 0.01 },
  support_campaign_currency: { type: 'select', options: ['USD', 'EUR', 'GBP'] },
  support_campaign_cooldown_days: { type: 'number', min: 1, max: 365, step: 1 },
  adaptive_dominant_style_enabled: { type: 'boolean' },
  adaptive_dominant_style_min_score: { type: 'number', min: 50, max: 100, step: 1 },
  adaptive_receptive_style_enabled: { type: 'boolean' },
  adaptive_power_style_max_intensity: { type: 'number', min: 1, max: 5, step: 1 },
  adaptive_interaction_style_enabled: { type: 'boolean' },
  adaptive_interaction_style_min_confidence: { type: 'number', min: 50, max: 100, step: 1 },
}

export function withMonetizationRouteDefaults(settings = {}) {
  return { ...MONETIZATION_ROUTE_SETTING_DEFAULTS, ...(settings || {}) }
}

function translate(t, key, fallback) {
  if (typeof t !== 'function') return fallback
  const result = t(key)
  return result && result !== key ? result : fallback
}

function readBool(value) {
  if (typeof value === 'boolean') return value
  if (typeof value === 'number') return value !== 0
  if (typeof value === 'string') return !['', '0', 'false', 'no', 'off'].includes(value.trim().toLowerCase())
  return Boolean(value)
}

function ClassicInput({ fieldKey, meta, value, label, onChange }) {
  if (meta.type === 'boolean') {
    return (
      <Switch
        checked={readBool(value)}
        onChange={(checked) => onChange(fieldKey, checked)}
        ariaLabel={label}
        className="setting-switch"
      />
    )
  }

  if (meta.type === 'select') {
    return (
      <Select
        options={meta.options.map((option) => ({ value: option, label: option }))}
        value={value || meta.options[0]}
        onChange={(next) => onChange(fieldKey, next)}
        className="setting-select"
      />
    )
  }

  if (meta.type === 'textarea') {
    return (
      <textarea
        rows={meta.rows || 4}
        value={String(value ?? '')}
        onChange={(event) => onChange(fieldKey, event.target.value)}
        className="setting-value"
      />
    )
  }

  return (
    <input
      type="number"
      min={meta.min}
      max={meta.max}
      step={meta.step}
      value={String(value ?? '')}
      onChange={(event) => onChange(fieldKey, event.target.value)}
      className="setting-value"
    />
  )
}

function ClassicField({ fieldKey, settings, onChange, t }) {
  const meta = FIELD_META[fieldKey]
  const label = translate(t, `agencySettings.setting.${fieldKey}`, fieldKey)
  const description = translate(t, `agencySettings.settingDesc.${fieldKey}`, '')
  return (
    <div className="setting-row">
      <div className="setting-label-wrap">
        <label className="setting-key">{label}</label>
        <span className="setting-desc">{description}</span>
      </div>
      <ClassicInput fieldKey={fieldKey} meta={meta} value={settings[fieldKey]} label={label} onChange={onChange} />
    </div>
  )
}

function V2Input({ fieldKey, meta, value, onChange, t }) {
  if (meta.type === 'boolean') {
    return (
      <label className="v2-check-field">
        <input type="checkbox" checked={readBool(value)} onChange={(event) => onChange(fieldKey, event.target.checked)} />
        <span>
          {readBool(value)
            ? translate(t, 'agencySettings.monetizationRoutes.enabled', 'Enabled')
            : translate(t, 'agencySettings.monetizationRoutes.disabled', 'Disabled')}
        </span>
      </label>
    )
  }

  const inputLabel = translate(t, 'agencySettings.monetizationRoutes.value', 'Value')
  if (meta.type === 'textarea') {
    return (
      <label className="v2-field">
        <span>{inputLabel}</span>
        <textarea rows={meta.rows || 4} value={String(value ?? '')} onChange={(event) => onChange(fieldKey, event.target.value)} />
      </label>
    )
  }

  if (meta.type === 'select') {
    return (
      <label className="v2-field">
        <span>{inputLabel}</span>
        <select value={value || meta.options[0]} onChange={(event) => onChange(fieldKey, event.target.value)}>
          {meta.options.map((option) => <option key={option} value={option}>{option}</option>)}
        </select>
      </label>
    )
  }

  return (
    <label className="v2-field">
      <span>{inputLabel}</span>
      <input
        type="number"
        min={meta.min}
        max={meta.max}
        step={meta.step}
        value={String(value ?? '')}
        onChange={(event) => onChange(fieldKey, event.target.value)}
      />
    </label>
  )
}

function V2Field({ fieldKey, settings, onChange, t, badge }) {
  const meta = FIELD_META[fieldKey]
  const label = translate(t, `agencySettings.setting.${fieldKey}`, fieldKey)
  const description = translate(t, `agencySettings.settingDesc.${fieldKey}`, '')
  return (
    <article className="v2-setting-field-card">
      <div className="v2-setting-field-head">
        <div>
          <span>{fieldKey}</span>
          <strong>{label}</strong>
        </div>
        <StatusPill tone="warn">{badge}</StatusPill>
      </div>
      <p>{description}</p>
      <V2Input fieldKey={fieldKey} meta={meta} value={settings[fieldKey]} onChange={onChange} t={t} />
    </article>
  )
}

export default function MonetizationRoutesSettings({ settingsDraft = {}, onChange, variant = 'classic', t: tProp = null }) {
  const { t: contextT } = useI18n()
  const t = tProp || contextT
  const settings = withMonetizationRouteDefaults(settingsDraft)
  const update = typeof onChange === 'function' ? onChange : () => {}

  if (variant === 'v2') {
    return (
      <div className="v2-stack">
        {ROUTE_GROUPS.map((group) => {
          const badge = translate(t, `agencySettings.monetizationRoutes.badge.${group.badge}`, group.badge)
          return (
            <section className="v2-stack" key={group.key}>
              <div className="v2-section-head">
                <div>
                  <h3>{translate(t, `agencySettings.monetizationRoutes.${group.key}.title`, group.key)}</h3>
                  <p>{translate(t, `agencySettings.monetizationRoutes.${group.key}.description`, '')}</p>
                </div>
              </div>
              <div className="v2-settings-grid">
                {group.keys.map((fieldKey) => (
                  <V2Field key={fieldKey} fieldKey={fieldKey} settings={settings} onChange={update} t={t} badge={badge} />
                ))}
              </div>
            </section>
          )
        })}
      </div>
    )
  }

  return (
    <>
      <div className="settings-section-block">
        <div className="settings-section-heading">
          <h3>{translate(t, 'agencySettings.monetizationRoutes.title', 'Monetization routes')}</h3>
          <p className="settings-hint">{translate(t, 'agencySettings.monetizationRoutes.description', 'Optional sales routes remain disabled until explicitly enabled for this configuration.')}</p>
        </div>
      </div>
      {ROUTE_GROUPS.map((group) => (
        <div className="settings-section-block" key={group.key}>
          <div className="settings-section-heading">
            <h3>{translate(t, `agencySettings.monetizationRoutes.${group.key}.title`, group.key)}</h3>
            <p className="settings-hint">{translate(t, `agencySettings.monetizationRoutes.${group.key}.description`, '')}</p>
          </div>
          <div className="settings-grid">
            {group.keys.map((fieldKey) => (
              <ClassicField key={fieldKey} fieldKey={fieldKey} settings={settings} onChange={update} t={t} />
            ))}
          </div>
        </div>
      ))}
    </>
  )
}
